// src/i18n/TranslatedText.js
//
// Shows free text (a note, a job description, scope...) in the viewer's
// language.
//
//   <TranslatedText text={note.text} hint={note.lang} />
//
//   // When the text needs formatting after translating:
//   <TranslatedText
//     text={job.jobDescription}
//     render={(s) => parseFormattedJobDescription(s)}
//     block
//   />
//
// - Spanish mode: everything not already Spanish is translated.
// - English mode: Spanish notes are translated; English is left alone.
// - A small "Translated · Show original" line lets anyone see exactly what
//   was typed. The stored text is never changed.
// - `hint` = the note's `lang` tag ("en"/"es") when it has one.
// - `showToggle={false}` hides the toggle line (for short labels).

import React, { useEffect, useState } from "react";
import { useLanguage } from "./LanguageContext";
import { needsTranslation, peekTranslation, translateText } from "./translateClient";

/**
 * Hook form: returns { text, translated, loading } for one string.
 * Use inside your own component when <TranslatedText> doesn't fit.
 */
export const useTranslatedText = (text, hint) => {
  const { lang } = useLanguage();
  const original = text === null || text === undefined ? "" : String(text);
  const wanted = needsTranslation(original, lang, hint);

  const cached = wanted ? peekTranslation(original, lang) : null;
  const [result, setResult] = useState(cached);

  useEffect(() => {
    let alive = true;
    if (!wanted) {
      setResult(null);
      return undefined;
    }
    const hit = peekTranslation(original, lang);
    if (hit) {
      setResult(hit);
      return undefined;
    }
    setResult(null);
    translateText(original, lang).then((r) => {
      if (alive) setResult(r);
    });
    return () => {
      alive = false;
    };
  }, [original, lang, wanted]);

  if (!wanted) return { text: original, translated: false, loading: false };
  if (!result) return { text: original, translated: false, loading: true };
  const changed = result.translated && result.translated.trim() !== original.trim();
  return { text: changed ? result.translated : original, translated: changed, loading: false };
};

const toggleStyle = {
  display: "block",
  marginTop: 2,
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  fontStyle: "italic",
};

const linkStyle = {
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  color: "#1d4ed8",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
  textDecoration: "underline",
  width: "auto",
  minHeight: 0,
  boxShadow: "none",
};

const TranslatedText = ({ text, hint, render, block = false, showToggle = true, as }) => {
  const { t } = useLanguage();
  const { text: shown, translated, loading } = useTranslatedText(text, hint);
  const [showOriginal, setShowOriginal] = useState(false);

  const original = text === null || text === undefined ? "" : String(text);
  const value = translated && showOriginal ? original : shown;
  const body = render ? render(value) : value;

  const Wrapper = as || (block ? "div" : "span");

  return (
    <Wrapper>
      {body}
      {showToggle && translated && (
        <span style={toggleStyle}>
          {showOriginal ? null : `${t("Translated")} · `}
          <button
            type="button"
            style={linkStyle}
            onClick={(e) => {
              e.stopPropagation();
              setShowOriginal((v) => !v);
            }}
          >
            {showOriginal ? t("Show translation") : t("Show original")}
          </button>
        </span>
      )}
      {showToggle && loading && <span style={toggleStyle}>{t("Translating…")}</span>}
    </Wrapper>
  );
};

export default TranslatedText;
